import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { startSingleCardCheckout, startCreditPackCheckout, startBusinessCheckout } from "@/lib/upgrade";
import { Check, Crown, Coins, Loader2, Store, Send, Building2, Zap } from "lucide-react";

const Subscription = () => {
  const { refreshUser } = useAuth();
  const [creditPacks, setCreditPacks] = useState([]);
  const [businessPlanInfo, setBusinessPlanInfo] = useState(null);
  const [sub, setSub] = useState(null);
  const [creditInfo, setCreditInfo] = useState(null);
  const [buyingCard, setBuyingCard] = useState(false);
  const [buyingPack, setBuyingPack] = useState("");
  const [businessLoading, setBusinessLoading] = useState(false);

  const load = useCallback(async () => {
    const [p, s, c] = await Promise.all([api.get("/subscription/plans"), api.get("/subscription"), api.get("/credits")]);
    setCreditPacks(p.data.credit_packs || []);
    setBusinessPlanInfo(p.data.business_plan);
    setSub(s.data);
    setCreditInfo(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Handle Stripe redirect return: verify credit pack / single card / business plan / tier purchase.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    const creditStatus = params.get("credit_status");
    const cardStatus = params.get("card_status");
    const tierStatus = params.get("tier_status");
    const sessionId = params.get("session_id");
    if (status === "success" && sessionId) {
      (async () => {
        try { await api.get(`/business/verify/${sessionId}`); await load(); await refreshUser(); } catch (e) { console.error("Business plan verify failed:", e); }
        window.history.replaceState({}, "", "/subscription");
      })();
    } else if (creditStatus === "success" && sessionId) {
      (async () => {
        try { await api.get(`/credits/verify/${sessionId}`); await load(); await refreshUser(); } catch (e) { console.error("Credit pack verify failed:", e); }
        window.history.replaceState({}, "", "/subscription");
      })();
    } else if (tierStatus === "success" && sessionId) {
      (async () => {
        try { await api.get(`/subscription/tier-verify/${sessionId}`); await load(); await refreshUser(); } catch (e) { console.error("Goliath/Royal tier verify failed:", e); }
        window.history.replaceState({}, "", "/subscription");
      })();
    } else if (cardStatus === "success") {
      (async () => { await load(); await refreshUser(); window.history.replaceState({}, "", "/subscription"); })();
    }
  }, [refreshUser, load]);

  const buySingleCard = async () => {
    setBuyingCard(true);
    try {
      const r = await startSingleCardCheckout();
      if (!r.redirected) { await load(); await refreshUser(); alert(`1 mailing credit added! Balance: ${r.data.mailing_credits}.`); }
    } finally { setBuyingCard(false); }
  };

  const buyPack = async (packId) => {
    setBuyingPack(packId);
    try {
      const r = await startCreditPackCheckout(packId);
      if (!r.redirected) { await load(); await refreshUser(); alert(`${r.data.added} credits added! Premium unlocked.`); }
    } catch (err) {
      const d = err?.response?.data?.detail;
      alert((d && typeof d === "object" ? d.message : d) || "Could not start checkout. Please try again.");
    } finally { setBuyingPack(""); }
  };

  const goBusiness = async () => {
    setBusinessLoading(true);
    try {
      const r = await startBusinessCheckout();
      if (!r.redirected) { await load(); await refreshUser(); }
    } finally { setBusinessLoading(false); }
  };

  const cancelBusiness = async () => {
    if (!window.confirm("Cancel your Business Team Plan?")) return;
    setBusinessLoading(true);
    try { await api.post("/business/cancel"); await load(); await refreshUser(); }
    finally { setBusinessLoading(false); }
  };

  const businessActive = sub?.business_plan_active;

  return (
    <AppShell>
      <div className="mb-8 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/15 border border-primary/30 text-sm mb-4">
          <Crown className="w-4 h-4 gold-text" /> Billing &amp; Credits
        </div>
        <h1 className="font-serif text-4xl mb-2">Your Gifted Account</h1>
        <p className="text-muted-foreground">Pay-per-card, a one-time Credit Pack, or go Business.</p>
      </div>

      {/* Boutique — pay-per-card */}
      <div className="surface rounded-2xl p-6 mb-6 max-w-xl mx-auto" data-testid="boutique-checkout-card">
        <div className="flex items-center gap-2 mb-1"><Store className="w-5 h-5 text-primary" /><h3 className="font-serif text-xl">Boutique — pay per card</h3></div>
        <p className="text-sm text-muted-foreground mb-4">Digital delivery is always free. Physical mailing is $9.99/card — the Silver Seal &amp; Maison Guest Note.</p>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs surface rounded-full px-3 py-1">Mailing credits: <span className="gold-text font-medium">{creditInfo?.mailing_credits ?? 0}</span></span>
          <button data-testid="buy-single-card-btn" onClick={buySingleCard} disabled={buyingCard}
            className="flex items-center gap-2 text-sm bg-gradient-to-r from-secondary to-primary text-white rounded-2xl px-4 py-2 hover:opacity-90 disabled:opacity-60">
            {buyingCard ? <><Loader2 className="w-4 h-4 animate-spin" /> …</> : <><Send className="w-4 h-4" /> Send a card · $9.99</>}
          </button>
        </div>
      </div>

      {/* Premium status / Credit Packs */}
      <div className="surface rounded-2xl p-6 mb-6 max-w-xl mx-auto" data-testid="premium-status-card">
        <div className="flex items-center gap-2 mb-1"><Zap className="w-5 h-5 text-primary" /><h3 className="font-serif text-xl">Premium Features</h3></div>
        {sub?.premium_unlocked ? (
          <div className="flex items-center gap-2 text-secondary text-sm mb-2" data-testid="premium-unlocked-badge"><Check className="w-4 h-4" /> Unlocked — Magic Wand, Circles, Royal Voice, Cash Gifting & more.</div>
        ) : (
          <p className="text-sm text-muted-foreground mb-4">Buy ANY Credit Pack once to permanently unlock every premium feature — plus a better per-card mailing rate.</p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
          {creditPacks.map((p) => (
            <div key={p.id} className="surface rounded-xl p-4 text-center" data-testid="credit-pack">
              <div className="font-serif text-2xl gold-text">${p.price}</div>
              <div className="text-sm text-muted-foreground mb-3">{p.credits} cards</div>
              <button data-testid="buy-credits-btn" onClick={() => buyPack(p.id)} disabled={!!buyingPack}
                className="w-full flex items-center justify-center gap-1.5 bg-gradient-to-r from-primary to-secondary text-white rounded-xl py-2 text-sm hover:opacity-90 disabled:opacity-60">
                {buyingPack === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />} Buy
              </button>
            </div>
          ))}
        </div>
        <p className="text-xs text-center text-muted-foreground mt-3">Secured by Stripe · credits + premium unlock added immediately on payment.</p>
      </div>

      {/* Business Team Plan */}
      <div className="surface rounded-2xl p-6 max-w-xl mx-auto" data-testid="business-plan-card">
        <div className="flex items-center gap-2 mb-1"><Building2 className="w-5 h-5 text-primary" /><h3 className="font-serif text-xl">Business Team Plan</h3></div>
        {businessActive ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-secondary text-sm"><Check className="w-4 h-4" /> Active — Bulk CSV, Scheduled Sends, Corporate Seal, Dashboard</div>
            <button data-testid="cancel-business-btn" onClick={cancelBusiness} disabled={businessLoading} className="text-xs text-muted-foreground hover:text-red-400">Cancel</button>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-4">{(businessPlanInfo?.features || []).join(" · ")}</p>
            <button data-testid="business-subscribe-btn" onClick={goBusiness} disabled={businessLoading}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#c9a24a] to-primary text-white rounded-2xl py-3 hover:opacity-90 disabled:opacity-60">
              {businessLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>Go Business — ${businessPlanInfo?.price ?? 29}/mo</>}
            </button>
          </>
        )}
        <p className="text-xs text-center text-muted-foreground mt-3">Manage your Club Captain org under Club Captain in the sidebar.</p>
      </div>
    </AppShell>
  );
};

export default Subscription;
